import { ImgHTMLAttributes } from "react";

interface OptimizedImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'loading' | 'decoding' | 'fetchPriority'> {
  /** Image source URL */
  src: string;
  /** Accessible alt text. Use "" for decorative images. */
  alt: string;
  /** Intrinsic width of the image in pixels — required for CLS prevention */
  width: number;
  /** Intrinsic height of the image in pixels — required for CLS prevention */
  height: number;
  /** Mark as LCP / above-the-fold image: sets loading="eager" + fetchPriority="high" */
  priority?: boolean;
  /** Mark as purely decorative: sets alt="" + aria-hidden="true" */
  decorative?: boolean;
  /**
   * Responsive sizes attribute for the browser to pick the right source.
   * Example: "(max-width: 768px) 100vw, 50vw"
   * Only meaningful when srcSet is also provided.
   */
  sizes?: string;
}

/**
 * Standardised image component that enforces performance and accessibility
 * best practices site-wide:
 *
 * - Explicit width/height to eliminate CLS
 * - `loading="lazy"` + `decoding="async"` by default (below-fold)
 * - `priority` flag for the single LCP image (eager + high fetchPriority)
 * - `decorative` flag for background / cosmetic images (alt="" + aria-hidden)
 * - Accepts optional `srcSet` + `sizes` for responsive serving
 */
export const OptimizedImage = ({
  src,
  alt,
  width,
  height,
  priority = false,
  decorative = false,
  sizes,
  className,
  srcSet,
  ...rest
}: OptimizedImageProps) => {
  return (
    <img
      src={src}
      alt={decorative ? "" : alt}
      width={width}
      height={height}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      {...(priority ? { fetchpriority: "high" } : {})}
      {...(decorative ? { "aria-hidden": true as const } : {})}
      {...(srcSet ? { srcSet } : {})}
      {...(sizes ? { sizes } : {})}
      className={className}
      {...rest}
    />
  );
};
